import { Alert, Button, Card, Checkbox, Dropdown, Empty, Pagination, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import debounce from 'lodash.debounce';
import moment from 'moment';
import { MagnifyingGlass } from 'phosphor-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import InputText from '~/components/InputText';
import { useSpecialFeatureUnlock } from '~/contexts/SpecialFeatureUnlockContext';
import api from '~/services/api';
import history from '~/services/history';

import { Container } from './styles';

export default function Assessment({ location }) {
  const { isSpecialFeatureEnabled, isKonamiUnlocked } = useSpecialFeatureUnlock();
  const shouldShowLuisMatthewsRows = !isSpecialFeatureEnabled || isKonamiUnlocked;
  const [assessment, setAssessment] = useState([]);
  const [filterStatus, setFilterStatus] = useState(['partially_received', 'pending_assessment']);
  const [filterCriteria, setFilterCriteria] = useState('receiving_id');

  const [visible, setVisible] = useState(false);

  const [paginationSize, setPaginationSize] = useState(new URLSearchParams(location.search).get('size') || 25);
  const [paginationPage, setPaginationPage] = useState(new URLSearchParams(location.search).get('page') || 0);
  const [paginationTotalItems, setPaginationTotalItems] = useState();

  const [loading, setLoading] = useState(true);

  const searchTextRef = useRef('');

  const { Title } = Typography;
  const { Option } = Select;

  const tagColor = {
    complete: {
      label: 'Assessment Complete',
      color: 'green',
    },
    complete_receiving_parts: {
      label: 'Complete Receiving of Parts',
      color: 'green',
    },
    pending_assessment: {
      label: 'Assessment Pending',
      color: 'yellow',
    },
    pending_receiving: {
      label: 'Receiving Pending',
      color: 'blue',
    },
    partially_received: {
      label: 'Partially Received',
      color: 'yellow',
    },
    null: {
      label: 'No Status',
      color: 'magenta',
    },
  };

  const totalFilterStatuses = Object.keys(tagColor).length;

  const selectedStatusesLabel = useMemo(() => {
    if (filterStatus.length === 0 || filterStatus.length === totalFilterStatuses) {
      return 'All Statuses';
    }

    return `${filterStatus.length} Selected`;
  }, [filterStatus.length, totalFilterStatuses]);

  const filteredAssessment = useMemo(() => {
    if (shouldShowLuisMatthewsRows) return assessment;

    return assessment.filter((item) => {
      const supplierName = item?.receiving?.purchase_order_ref?.supplier?.name;
      if (!supplierName) return true;
      return supplierName.trim().toLowerCase() !== 'luis matthews';
    });
  }, [assessment, shouldShowLuisMatthewsRows]);

  const displayedPaginationTotalItems = useMemo(() => {
    if (shouldShowLuisMatthewsRows) return paginationTotalItems;

    const hiddenRowsInCurrentPage = assessment.length - filteredAssessment.length;
    return Math.max(0, (paginationTotalItems || 0) - hiddenRowsInCurrentPage);
  }, [filteredAssessment.length, assessment.length, shouldShowLuisMatthewsRows, paginationTotalItems]);

  const formatSerialNumberSearchValue = useCallback((rawValue = '') => {
    const normalizedValue = rawValue.trim().toUpperCase();
    if (!normalizedValue.startsWith('P/N:')) {
      return normalizedValue;
    }

    const serialMarkerIndex = normalizedValue.indexOf('S/N:');
    if (serialMarkerIndex === -1) {
      return normalizedValue;
    }

    return normalizedValue.substring(serialMarkerIndex + 4);
  }, []);

  const handleSearch = useCallback(
    async (value) => {
      try {
        setLoading(true);

        // Format search value
        const valueTrimmed = (value || searchTextRef.current?.value || '').trim().toUpperCase();
        const serialNumberFormatted = formatSerialNumberSearchValue(valueTrimmed);

        if (searchTextRef.current) {
          searchTextRef.current.value = serialNumberFormatted || '';
        }

        // Make API requests
        const [receivingResponse, additionalItemsResponse] = await Promise.all([
          api.get(`receivingItems`, {
            params: {
              size: paginationSize,
              page: paginationPage,
            },
            headers: {
              filterCriteria,
              filterText: serialNumberFormatted || '',
              filterStatus: filterStatus.join(',') || '',
            },
          }),
          api.get('receivingItemsParts?assessflag=true'),
        ]);

        if (receivingResponse.data.error) {
          throw new Error(receivingResponse.data.error);
        }

        // Process assessment list
        const assessmentList = receivingResponse.data.rows.map((row) => ({
          ...row,
          additional_item: additionalItemsResponse.data.some((item) => item.receiving_item_id === row.id),
        }));

        // Update state
        setPaginationTotalItems(receivingResponse.data.totalItems);
        setPaginationSize(receivingResponse.data.sizePage);
        setPaginationPage(receivingResponse.data.currentPage);
        setAssessment(assessmentList);
      } catch (error) {
        toast.error(`Error loading assessments: ${error.message}`);
        setAssessment([]);
        setPaginationTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [filterStatus, paginationPage, paginationSize, filterCriteria, formatSerialNumberSearchValue]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        handleSearch(value);
      }, 500),
    [handleSearch]
  );

  useEffect(() => {
    const { search } = location;
    const params = new URLSearchParams(search);
    if (params.get('filterCriteria')) {
      setFilterCriteria(params.get('filterCriteria'));
    }

    const searchQuery = params.get('searchQuery');
    if (searchQuery) {
      searchTextRef.current.value = searchQuery;
      handleSearch(searchQuery);
    } else if (searchTextRef.current) {
      // Clear searchTextRef if no search query
      searchTextRef.current.value = '';
    }
  }, [location.search, handleSearch]); //eslint-disable-line

  const handleChangePage = useCallback((page, pageSize) => {
    setPaginationPage(page);
    setPaginationSize(pageSize);
  }, []);

  useEffect(() => {
    // For serial_number, only search if we have a search term
    // For receiving_id, always search (even without term)
    // For other criteria, search if we have a term or are not in serial_number mode
    if (
      filterCriteria === 'receiving_id' ||
      (filterCriteria === 'serial_number' && searchTextRef.current?.value?.trim()) ||
      (filterCriteria !== 'serial_number' && filterCriteria !== 'receiving_id' && searchTextRef.current?.value?.trim())
    ) {
      handleSearch(searchTextRef.current?.value);
    }
  }, [handleSearch, paginationPage, paginationSize, filterCriteria]);

  const getLotAssessmentUrl = useCallback(
    (record) =>
      `/lotAssessment?id=${record.id}&receiving_id=${record.receiving_id}&filterCriteria=${filterCriteria || 'receiving_id'}&searchQuery=${
        searchTextRef.current.value
      }&size=${paginationSize}&page=${paginationPage}`,
    [filterCriteria, paginationSize, paginationPage]
  );

  const lotAssessment = (record) => {
    history.push(getLotAssessmentUrl(record));
  };

  const additionalAssessment = (lotId) => {
    history.push(
      `/lotAssessment?id=${lotId}&filterCriteria=${filterCriteria || 'receiving_id'}&searchQuery=${
        searchTextRef.current.value
      }&size=${paginationSize}&page=${paginationPage}&additionalFlag=true`
    );
  };

  const columns = [
    {
      title: 'Receiving id',
      dataIndex: 'receiving_id',
      width: 100,
    },
    {
      title: 'PO #',
      dataIndex: ['receiving', 'purchase_order_ref', 'po_number'],
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
    },
    {
      title: 'Supplier',
      dataIndex: ['receiving', 'purchase_order_ref', 'supplier', 'name'],
    },
    {
      title: 'Quantity Received',
      dataIndex: 'quantity',
      width: 160,
    },
    {
      title: 'Quantity to Receive',
      dataIndex: 'quantity_to_receive',
      width: 160,
    },
    {
      title: 'Received',
      dataIndex: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (text) => moment(text).fromNow(),
      width: 200,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text) => (
        <Tag color={tagColor[text]?.color} key={tagColor[text]?.label}>
          {tagColor[text]?.label.toUpperCase() || text}
        </Tag>
      ),
      width: 200,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_text, record) => {
        const lotAssessmentUrl = getLotAssessmentUrl(record);

        const openLotAssessmentInNewTab = () => {
          window.open(lotAssessmentUrl, '_blank', 'noopener,noreferrer');
        };

        const handleLotAssessmentMouseDown = (event) => {
          // `auxclick` on buttons is inconsistent; use mouse down for middle-click support.
          if (event.button === 1) {
            event.preventDefault();
            openLotAssessmentInNewTab();
          }
        };

        const handleLotAssessmentClick = (event) => {
          if (event.metaKey || event.ctrlKey) {
            event.preventDefault();
            openLotAssessmentInNewTab();
            return;
          }

          lotAssessment(record);
        };

        return (
          <Space>
            <Button
              disabled={record.status === 'pending_receiving'}
              type="primary"
              className="button-error pure-button"
              onMouseDown={handleLotAssessmentMouseDown}
              onClick={handleLotAssessmentClick}
            >
              Lot Assessment
            </Button>
          </Space>
        );
      },
      // width: 250,
    },
    {
      title: 'Additional Items',
      dataIndex: 'additional_item',
      render: (text, record) => (
        <Button
          disabled={!text}
          type="primary"
          className="button-error pure-button"
          onClick={() => additionalAssessment(record.id)}
          style={{ backgroundColor: '#3EB19E', borderColor: '#3EB19E' }}
        >
          Additional
        </Button>
      ),
    },
  ];

  const handleVisibleChange = (flag) => {
    setVisible(flag);
  };

  const renderAssessmentContent = () => {
    if (loading) {
      return (
        <div style={{ marginTop: '20px' }}>
          <Spin tip="Loading items...">
            <Alert
              message="Searching for assessment items"
              description="Please wait while we retrieve the results. Use more specific search terms for faster results."
              type="info"
              showIcon
            />
          </Spin>
        </div>
      );
    }

    if (filteredAssessment.length <= 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>Use the search box above to list assessment</span>}
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            height: '100%',
          }}
        />
      );
    }

    return (
      <Table
        style={{ marginTop: '5px' }}
        columns={columns}
        dataSource={filteredAssessment}
        size="small"
        bordered
        pagination={false}
        scroll={{ y: 'calc(100vh - 280px)' }}
        rowKey={(record) => record.id}
        loading={loading}
        locale={{
          emptyText: searchTextRef.current?.value ? 'No items found matching your search criteria' : 'No items to display',
        }}
      />
    );
  };

  const filterMenuItems = [
    {
      key: 'filter-group',
      label: (
        <Checkbox.Group
          onChange={(value) => {
            setFilterStatus(value);
            handleSearch(); // Immediately trigger search on status change
            // setVisible(false); // Close dropdown after selection
          }}
          value={filterStatus}
        >
          <Space direction="vertical">
            {Object.entries(tagColor)
              // remove the 'null' status from the filter options
              .filter(([key]) => key !== 'null')
              .map(([key, value]) => (
                <Checkbox key={key} value={key}>
                  {value.label}
                </Checkbox>
              ))}
          </Space>
        </Checkbox.Group>
      ),
    },
  ];

  function handleChangeFilterCriteria(value) {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('filterCriteria', value);

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
    setFilterCriteria(value);
    setPaginationPage(0);
    searchTextRef.current.focus();

    // Only trigger search if there's a value in the search box
    if (searchTextRef.current?.value?.trim()) {
      handleSearch(searchTextRef.current.value);
    }
  }

  return (
    <Container style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Card size="small" bordered={false}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div />
          <Title level={3}>Receiving Items to Assess</Title>
          <div />
        </div>
      </Card>
      <Card elevation={3} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space wrap>
            <Select onChange={handleChangeFilterCriteria} value={filterCriteria || 'receiving_id'} style={{ width: 160 }}>
              <Option value="receiving_id">Receiving id</Option>
              <Option value="purchase_order">Purchase Order #</Option>
              <Option value="serial_number">Serial Number</Option>
              <Option value="supplier">Supplier</Option>
              <Option value="description">Description</Option>
            </Select>
            <InputText
              placeholder="Search by ID, PO #, Description, or Supplier"
              ref={searchTextRef}
              onBlur={(e) => {
                const value = e.target.value || '';
                const formatted = formatSerialNumberSearchValue(value);
                e.target.value = formatted;
                if (formatted !== value) {
                  debouncedSearch(formatted);
                }
              }}
              autoFocus
              onPressEnter={(value) => debouncedSearch(value)}
              onChange={(e) => {
                if (!e.target.value.trim()) {
                  debouncedSearch('');
                }
              }}
              style={{ width: 300 }}
              IconAfterProp={
                loading ? <Spin size="small" /> : <MagnifyingGlass cursor="pointer" onClick={() => debouncedSearch(searchTextRef.current.value)} size={20} />
              }
            />
            {filterCriteria === 'serial_number' && (
              <Alert
                // message="Status filters are disabled for serial number searches"
                description="Serial number searches will return all matching items regardless of their status"
                type="info"
                showIcon
              />
            )}
          </Space>
          <Space>
            <Dropdown
              menu={{ items: filterMenuItems }}
              trigger={['click']}
              onOpenChange={handleVisibleChange}
              open={visible}
              overlayStyle={{ padding: '12px' }}
            >
              <Button
                className="ant-dropdown-link"
                style={{ width: 140 }}
                onClick={() => setVisible(true)}
                type={filterStatus.length < totalFilterStatuses ? 'primary' : 'default'}
              >
                {selectedStatusesLabel}
              </Button>
            </Dropdown>
            {filterStatus.length > 0 && filterStatus.length < totalFilterStatuses && (
              <Tag
                closable
                color="blue"
                onClose={(e) => {
                  e.preventDefault();
                  setFilterStatus(['partially_received', 'pending_assessment']);
                  handleSearch();
                }}
              >
                Clear Status Filter
              </Tag>
            )}
          </Space>
        </div>
      </Card>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>{renderAssessmentContent()}</div>
        <Card
          size="small"
          bordered={false}
          style={{
            marginTop: '5px',
            position: 'sticky',
            bottom: 0,
            backgroundColor: '#fff',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.15)',
            zIndex: 10,
          }}
        >
          <Pagination
            size="small"
            total={displayedPaginationTotalItems}
            showSizeChanger
            showQuickJumper
            defaultPageSize={paginationSize}
            pageSizeOptions={[25, 50, 100]}
            onChange={handleChangePage}
            current={paginationPage}
          />
        </Card>
      </div>
    </Container>
  );
}
